/**
 * Starts a configured action button's submit flow.
 *
 * A submit action writes a row, so it needs a signed-in user. Rather than
 * inventing an auth path, this reuses the one Form Builder already uses:
 * redirect to /sign-in?next=<here>, and SignInPage navigates back afterwards.
 * That page already validates `next` starts with a single "/" — the guard that
 * stops an open redirect — so the return trip is safe by construction.
 *
 * The action id rides along in `?resume_action=`, so the button can pick up
 * where it left off once the visitor is back. Only the ID travels: the payload
 * is assembled from data the page already trusts and the row is written by the
 * server from the record id in the URL, so a tampered return link can at worst
 * name a different button on the same record — never a different record, table
 * or amount.
 */

export const RESUME_PARAM = 'resume_action';
const STASH_KEY = 'arcee_pending_action';

/**
 * Holds a half-finished submission across the sign-in round trip.
 *
 * A full-page redirect to /sign-in discards everything typed, so without this
 * the visitor comes back to an empty form and has to fill it in twice — which
 * reads as the click having failed. sessionStorage is the right store: it is
 * per-tab and cleared when the tab closes, and the answers are the visitor's
 * own, so nothing is exposed that they did not just type themselves.
 */
export function stashPendingAction(buttonId, answers, payload) {
  try {
    sessionStorage.setItem(STASH_KEY, JSON.stringify({ buttonId, answers, payload }));
  } catch (e) { /* private mode: the visitor just refills the form */ }
}

/** Reads back a stash for `buttonId`, clearing it so a reload cannot resubmit. */
export function takePendingAction(buttonId) {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STASH_KEY);
    const parsed = JSON.parse(raw);
    return parsed && parsed.buttonId === buttonId ? parsed : null;
  } catch (e) { return null; }
}

/** Where the visitor should land after signing in. */
export function buildReturnPath(tableSlug, recordId, buttonId) {
  return `/p/${encodeURIComponent(tableSlug)}/${encodeURIComponent(recordId)}` +
         `?${RESUME_PARAM}=${encodeURIComponent(buttonId)}`;
}

/** The sign-in URL that returns here afterwards. */
export function buildSignInUrl(tableSlug, recordId, buttonId) {
  return `/sign-in?next=${encodeURIComponent(buildReturnPath(tableSlug, recordId, buttonId))}`;
}

/**
 * Runs a submit action, sending the visitor through sign-in first when needed.
 *
 * `isAuthenticated` is injected so this stays a pure decision that can be
 * tested without a session.
 */
export function startAction(button, { tableSlug, recordId, isAuthenticated, onSubmit } = {}) {
  if (!button || button.type !== 'submit') return 'ignored';

  if (!isAuthenticated) {
    // Remember the click so the return trip can finish it. Without this a
    // FORMLESS button loses the visitor's intent at the redirect: they come
    // back to the record with `?resume_action=`, nothing is stashed, and the
    // resume declines to fire — so the first click of a signed-out visit
    // registered nobody and said nothing. It took a second click.
    //
    // Only for a button with NO form. A form-backed button must keep arriving
    // back with an empty stash: its answers are stashed by submitAction when
    // the form is actually submitted, and stashing here would resume it with
    // `answers: {}` — silently posting a blank form the visitor never filled.
    //
    // The stash doubles as proof the visitor really clicked. A hand-typed or
    // stale `?resume_action=` link stashes nothing, so it still cannot trigger
    // a write — the guard that behaviour depends on is preserved, not removed.
    if (!button.action || !button.action.form_id) {
      stashPendingAction(button.id, {}, {});
    }
    window.location.href = buildSignInUrl(tableSlug, recordId, button.id);
    return 'redirected_to_sign_in';
  }

  if (onSubmit) onSubmit(button);
  return 'submitted';
}

/**
 * Posts a submit action to the server.
 *
 * The body carries ONLY the fields the button offers. The record, the table and
 * every trusted value (price, status) are decided server-side from the button's
 * configuration, so nothing structural travels from the browser.
 */
export function buildSubmitUrl({ orgId, appId, tableSlug, recordId, buttonId }) {
  return `/arcee/public/${orgId}/${appId}/${encodeURIComponent(tableSlug)}/` +
         `${encodeURIComponent(recordId)}/action/${encodeURIComponent(buttonId)}/`;
}

/**
 * Decides whether a 2xx response actually wrote something.
 *
 * A successful HTTP status is not the same claim as "the record is saved", and
 * telling a visitor "Done." when nothing reached the database is the worst of
 * the failure modes — they walk away believing they registered. So success is
 * read from what the server reports about the write, not from the status code.
 *
 * What counts as written: operations or refs came back, meaning rows were
 * created.
 *
 * Anything else — success flag absent or false, or an empty result — is treated
 * as a failure, because we cannot show a confirmation we cannot substantiate.
 */
export function confirmsWrite(body) {
  if (!body || body.success !== true) return false;

  const result = body.result;
  if (!result || typeof result !== 'object') return false;

  const wroteOperations = Array.isArray(result.operations) && result.operations.length > 0;
  const wroteRefs = result.refs && typeof result.refs === 'object'
    && Object.keys(result.refs).length > 0;

  return Boolean(wroteOperations || wroteRefs);
}

/**
 * Reads the button id a sign-in round trip asked us to resume.
 *
 * Returns null when the parameter is absent or does not name a configured
 * button — a stale or hand-edited link must not trigger something arbitrary.
 */
export function resumeButtonFrom(search, buttons) {
  const id = new URLSearchParams(search || '').get(RESUME_PARAM);
  if (!id) return null;
  const known = (buttons || []).some(b => b && b.id === id);
  return known ? id : null;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Completed actions — the outcome of the flow above, remembered.
 *
 * The server keeps the answer in the visitor's own session and hands it back on
 * the login check the public pages already make, as a flat list of keys shaped
 *
 *     <org_id>/<app_id>/<table_slug>/<record_id>/<button_id>
 *
 * Nothing in that key names an application, a table's meaning, or a button's
 * purpose — the same code serves an events table, a course catalogue or a
 * property listing without knowing which it is looking at.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Narrows the visitor's completed actions to the application AND table being
 * viewed, returning `<recordId>/<buttonId>` keys.
 *
 * THE ORG AND APP ARE PART OF THE KEY, and that is not decoration. One Django
 * session serves every application on a backend, so a visitor who uses two
 * applications carries one cookie across both. Without the org/app segments a
 * record 12 in app 4's `event` table would silently satisfy record 12 in app 9's
 * `event` table — a button hidden on an event the visitor has never seen. The
 * table slug is included for the same reason one level down: two tables in one
 * application may each call a button `register`.
 *
 * Unknown shapes are dropped rather than trusted. This list arrives over the
 * wire, and a malformed entry must fail to a VISIBLE button — never a hidden
 * one, which would block a genuine registration.
 */
export function completedKeysForTable(completedActions, orgId, appId, tableSlug) {
  const keys = new Set();
  if (!Array.isArray(completedActions)) return keys;
  if (!orgId || !appId || !tableSlug) return keys;

  const prefix = `${orgId}/${appId}/${tableSlug}/`;
  for (const entry of completedActions) {
    if (typeof entry !== 'string' || !entry.startsWith(prefix)) continue;
    const rest = entry.slice(prefix.length);
    // Exactly recordId/buttonId — no more, no less.
    if (rest.split('/').length !== 2) continue;
    if (rest.startsWith('/') || rest.endsWith('/')) continue;
    keys.add(rest);
  }
  return keys;
}

/** Adds a key locally, so the page updates without waiting for a refetch. */
export function withCompleted(keys, recordId, buttonId) {
  const next = new Set(keys);
  next.add(`${recordId}/${buttonId}`);
  return next;
}

/**
 * Reads the completed-action list off a login-check reply.
 *
 * Separated so the page never has to reason about the shape: a reply from a
 * server that predates this feature, a failed check that returned null, or a
 * field of the wrong type all produce the same empty list — which draws every
 * button, exactly as today.
 */
export function completedActionsFrom(loginCheck) {
  const value = loginCheck && loginCheck.completed_actions;
  return Array.isArray(value) ? value : [];
}
